/**
 * Single source of truth for bookmaker display names.
 * Covers standard brands, offshore books, and legacy key aliases.
 */
const DISPLAY_NAMES = {
  draftkings: 'DraftKings',
  fanduel: 'FanDuel',
  betmgm: 'BetMGM',
  caesars: 'Caesars',
  williamhill_us: 'Caesars',
  betrivers: 'BetRivers',
  bovada: 'Bovada',
  fanatics: 'Fanatics',
  espnbet: 'ESPN Bet',
  hardrockbet: 'Hard Rock Bet',
  mybookieag: 'MyBookie.ag',
  betonlineag: 'BetOnline.ag',
  betus: 'BetUS',
  lowvig: 'LowVig.ag',
  lowvigag: 'LowVig.ag',
};

/**
 * Return the display name for a bookmaker key. Falls back to the raw key.
 */
export function getBookmakerLabel(key) {
  return DISPLAY_NAMES[key] ?? key;
}

/**
 * Preferred bookmaker ordering — popular books first, then alphabetical.
 */
const PREFERRED_ORDER = [
  'draftkings',
  'fanduel',
  'betmgm',
  'betrivers',
  'bovada',
];

/**
 * Scan events and return a sorted array of { key, title } for all unique bookmakers.
 * Popular books come first in PREFERRED_ORDER, then the rest alphabetically by title.
 */
export function getBookmakerColumns(events) {
  const seen = new Map(); // key -> title
  for (const event of events) {
    for (const bm of event.bookmakers || []) {
      if (!seen.has(bm.key)) {
        seen.set(bm.key, DISPLAY_NAMES[bm.key] ?? bm.title ?? bm.key);
      }
    }
  }

  const entries = [...seen.entries()].map(([key, title]) => ({ key, title }));

  entries.sort((a, b) => {
    const ai = PREFERRED_ORDER.indexOf(a.key);
    const bi = PREFERRED_ORDER.indexOf(b.key);
    // Both preferred — sort by preferred order
    if (ai !== -1 && bi !== -1) return ai - bi;
    // One preferred, one not — preferred first
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    // Neither preferred — alphabetical by title
    return a.title.localeCompare(b.title);
  });

  return entries;
}

/**
 * Same as getBookmakerColumns but for a single event.
 */
export function getBookmakerColumnsFromEvent(event) {
  return getBookmakerColumns([event]);
}
